// src/pages/Channels/index.js
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  CheckCircleIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import {
  fetchChannels,
  updateChannelStatus,
} from "../../redux/slices/channelsSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { useTranslation } from "../../contexts/LanguageContext";
import Pagination from "../../components/Common/Pagination";
import ChannelFormModal from "../../components/Channels/ChannelFormModal";

const PAGE_SIZE = 10;

const STATUS_BADGE = {
  live:       "bg-green-100 text-green-800",
  active:     "bg-green-100 text-green-800",
  scheduled:  "bg-yellow-100 text-yellow-800",
  completed:  "bg-blue-100 text-blue-800",
  noshow:     "bg-orange-100 text-orange-800",
  cancelled:  "bg-red-100 text-red-800",
  blocked:    "bg-red-100 text-red-800",
  draft:      "bg-gray-100 text-gray-800",
  deleted:    "bg-red-100 text-red-800",
};

function ChannelsPage() {
  const dispatch = useDispatch();
  const { t, tStatus } = useTranslation();
  const { channels, total, totalPages, currentPage, loading, error } =
    useSelector((s) => s.channels);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(() => {
    dispatch(fetchChannels({
      currentPage: page,
      limit: PAGE_SIZE,
      byKeyword: searchTerm,
    }));
  }, [dispatch, page, searchTerm]);

  // Debounce search; refetch on page change.
  useEffect(() => {
    const id = setTimeout(refresh, 300);
    return () => clearTimeout(id);
  }, [refresh]);

  const handleAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (channel) => {
    setEditing(channel);
    setFormOpen(true);
  };

  const handleToggleBlock = async (channel) => {
    const next = channel.status === "blocked" ? "live" : "blocked";
    try {
      await dispatch(updateChannelStatus({ channelId: channel._id, status: next })).unwrap();
      dispatch(showNotification({ message: t("channelForm.statusUpdatedSuccess"), type: "success" }));
    } catch (err) {
      dispatch(showNotification({ message: err || t("channelForm.failedStatus"), type: "error" }));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;

  return (
    <div>
      <div className="bg-white rounded-[10px] border border-dashboardStroke shadow-[0px_9px_15px_rgba(0,0,0,0.07)] overflow-hidden">
        {/* Header */}
        <div className="bg-dashboardDark h-16 px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-white font-semibold text-lg">{t("channels.title")}</h2>
            <span className="text-white font-semibold">({total || 0})</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-white rounded">
              <MagnifyingGlassIcon className="w-[18px] h-[18px] text-white" />
              <input
                type="text"
                placeholder={t("channels.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="bg-transparent text-white placeholder-white/70 text-sm font-light outline-none w-44"
              />
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-3 py-1.5 bg-dashboardCoral border border-white rounded text-white text-sm font-medium"
            >
              <PlusIcon className="w-4 h-4" />
              <span>{t("channels.addNew")}</span>
            </button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
            <span className="ml-2 text-dashboardText">{t("channels.loading")}</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-dashboardText">{t("channels.noFound")}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dashboardStroke bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("col.srNo")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.title")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.creator")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.category")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.startTime")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.duration")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.bidderType")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.viewsCount")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.status")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.totalOrders")}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-dashboardText">{t("channels.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((c, i) => {
                    const creator = c.userId?.name || c.userId?.email || c.createdBy || t("channels.unknownCreator");
                    const creatorId = c.userId?._id || c.userId || c.createdBy;
                    const creatorHref = creatorId
                      ? `/users/${creatorId}?tab=channels&channelId=${c._id}`
                      : null;
                    const category = c.categoryId?.categoryName || "—";
                    const badge = STATUS_BADGE[c.status] || "bg-gray-100 text-gray-800";
                    const bidderTypeKey = `channels.bidderType.${c.bidderType}`;
                    return (
                      <tr key={c._id} className="border-b border-dashboardStroke hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{startIndex + i + 1}</td>
                        <td className="px-4 py-3 text-sm font-medium text-dashboardDarkText">
                          <div className="flex items-center gap-2">
                            {c.thumbnail && (
                              <img src={c.thumbnail} alt="" className="h-8 w-8 rounded object-cover" />
                            )}
                            <span className="truncate max-w-[14rem]" title={c.title}>
                              {c.title || t("channels.untitled")}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-dashboardCoral font-medium">
                          {creatorHref ? (
                            <Link to={creatorHref} className="hover:underline">{creator}</Link>
                          ) : creator}
                        </td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{category}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{fmtDate(c.startTime)}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">
                          {c.duration != null ? `${c.duration} ${t("channels.minutes")}` : "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{t(bidderTypeKey)}</td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">{c.viewsCount || 0}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${badge}`}>
                            {tStatus(c.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-dashboardDarkText">
                          {c.ordersCount || 0}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(c)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={t("channelForm.editTitle")}
                            >
                              <PencilIcon className="w-5 h-5 text-dashboardDark" />
                            </button>
                            <button
                              onClick={() => handleToggleBlock(c)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title={c.status === "blocked" ? t("allAuction.unblock") : t("allAuction.block")}
                            >
                              {c.status === "blocked"
                                ? <CheckCircleIcon className="w-5 h-5 text-green-600" />
                                : <NoSymbolIcon className="w-5 h-5 text-dashboardCoral" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-4 border-t border-dashboardStroke">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  total={total}
                  itemsPerPage={PAGE_SIZE}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ChannelFormModal
        isOpen={isFormOpen}
        setIsOpen={setFormOpen}
        channel={editing}
        currentPage={page}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
}

export default ChannelsPage;
